/**
 * Task A2A-D (#1962) — Tipi/Zod schema condivisi per A2A Gateway tab.
 *
 * Contratto frontend ↔ backend per:
 *   - GET /api/a2a/gateway/agents       → AgentCardsGrid (registry + stats)
 *   - GET /api/a2a/gateway/calls        → InterAgentCallsTable / ArchitectToolCallsTable
 *   - GET /api/a2a/gateway/stats        → Registry stats (header KPI)
 *   - GET /api/a2a/gateway/runs/:id/graph → RunWorkflowGraph (nodi + archi)
 *
 * Tutti i payload sono validati al boundary HTTP via Zod (no `any`).
 */
import { z } from 'zod';

export const A2A_CALL_TYPES = ['a2a', 'mcp'] as const;
export type A2ACallType = (typeof A2A_CALL_TYPES)[number];
export const A2ACallTypeSchema = z.enum(A2A_CALL_TYPES);

export const A2A_CALL_STATUSES = ['ok', 'error'] as const;
export type A2ACallStatus = (typeof A2A_CALL_STATUSES)[number];
export const A2ACallStatusSchema = z.enum(A2A_CALL_STATUSES);

// ---------------------------------------------------------------------------
// Call row (DB-shaped, ISO timestamps)
// ---------------------------------------------------------------------------
export const A2ACallLogRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  call_type: A2ACallTypeSchema,
  source_agent_id: z.string().uuid().nullable(),
  source_agent_slug: z.string().nullable(),
  source_agent_name: z.string().nullable(),
  target_agent_id: z.string().uuid().nullable(),
  target_agent_slug: z.string().nullable(),
  target_agent_name: z.string().nullable(),
  method_or_tool: z.string(),
  status: A2ACallStatusSchema,
  duration_ms: z.number().int().nullable(),
  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
  args_summary: z.unknown().nullable(),
  run_id: z.string().uuid().nullable(),
  hop_index: z.number().int().nullable(),
  caller_kind: z.string().nullable(),
  trace_id: z.string().nullable(),
  created_at: z.string(),
});
export type A2ACallLogRow = z.infer<typeof A2ACallLogRowSchema>;

// ---------------------------------------------------------------------------
// Agent card (registry + recent stats)
// ---------------------------------------------------------------------------
export const A2AAgentWithStatsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  agent_kind: z.string(),
  endpoint_url: z.string(),
  auth_mode: z.string(),
  is_active: z.boolean(),
  capabilities: z.unknown(),
  card_json: z.unknown(),
  created_at: z.string(),
  updated_at: z.string(),
  stats: z.object({
    calls_24h: z.number().int(),
    errors_24h: z.number().int(),
    avg_duration_ms_24h: z.number().int().nullable(),
    last_call_at: z.string().nullable(),
  }),
});
export type A2AAgentWithStats = z.infer<typeof A2AAgentWithStatsSchema>;

export const A2AAgentWithStatsListSchema = z.array(A2AAgentWithStatsSchema);
export type A2AAgentWithStatsList = z.infer<typeof A2AAgentWithStatsListSchema>;

// ---------------------------------------------------------------------------
// Registry stats (header KPI)
// ---------------------------------------------------------------------------
export const A2AGatewayStatsSchema = z.object({
  agents_total: z.number().int(),
  agents_active: z.number().int(),
  agents_by_kind: z.record(z.string(), z.number().int()),
  calls_24h: z.number().int(),
  errors_24h: z.number().int(),
  a2a_calls_24h: z.number().int(),
  mcp_calls_24h: z.number().int(),
  architect_runs_24h: z.number().int(),
  avg_duration_ms_24h: z.number().int().nullable(),
});
export type A2AGatewayStats = z.infer<typeof A2AGatewayStatsSchema>;

// ---------------------------------------------------------------------------
// Calls list query + response
// ---------------------------------------------------------------------------
export const A2ACallsListQuerySchema = z.object({
  call_type: A2ACallTypeSchema.optional(),
  run_id: z.string().uuid().optional(),
  status: A2ACallStatusSchema.optional(),
  agent_id: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  cursor: z.string().datetime().optional(),
});
export type A2ACallsListQuery = z.infer<typeof A2ACallsListQuerySchema>;

export const A2ACallsListResponseSchema = z.object({
  items: z.array(A2ACallLogRowSchema),
  next_cursor: z.string().nullable(),
});
export type A2ACallsListResponse = z.infer<typeof A2ACallsListResponseSchema>;

// ---------------------------------------------------------------------------
// Run workflow graph
// ---------------------------------------------------------------------------
export const A2ARunGraphNodeSchema = z.object({
  id: z.string(),
  kind: z.enum(['root', 'llm', 'a2a', 'mcp', 'error', 'note']),
  label: z.string(),
  ok: z.boolean(),
  duration_ms: z.number().int().nullable(),
  hop_index: z.number().int().nullable(),
  capability_ref: z.string().nullable(),
  capability_type: z.string().nullable(),
  error: z.string().nullable(),
});
export type A2ARunGraphNode = z.infer<typeof A2ARunGraphNodeSchema>;

export const A2ARunGraphEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
});
export type A2ARunGraphEdge = z.infer<typeof A2ARunGraphEdgeSchema>;

export const A2ARunGraphSchema = z.object({
  run_id: z.string().uuid(),
  status: z.string(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  root_message: z.string(),
  nodes: z.array(A2ARunGraphNodeSchema),
  edges: z.array(A2ARunGraphEdgeSchema),
});
export type A2ARunGraph = z.infer<typeof A2ARunGraphSchema>;
